import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TIDS } from "@/constants/testIds";
import { api } from "@/lib/api";
import { HeroParticles, Typewriter, useMouseParallax } from "@/components/HeroFX";
import { CommunityStatsRow, LiveActivityTicker, TrendingCreators, FriendStatusRail } from "@/components/LiveVibes";
import {
  Radio, Gamepad2, Sparkles, Zap, Users, Coins, ShieldCheck,
  Terminal, Rocket, MessageSquare, Flame, Trophy, ArrowRight, Cpu, Wifi, Eye,
} from "lucide-react";
import GlobalSearch from "@/components/GlobalSearch";
import ObsHlsPlayer from "@/components/ObsHlsPlayer";
// Founder marquee temporarily disabled. See Profile.jsx for details.

const GAME_NAMES = {
  ttt: "Tic-Tac-Toe", connect4: "Connect Four", rps: "Rock Paper Scissors",
  hangman: "Hangman", wordscramble: "Word Scramble", diceroll: "High Roller",
  coinflip: "Coin Flip", slots: "Slots", checkers: "Checkers",
  chess: "Chess", holdem: "Hold'em",
};

// Animated integer counter (framer-motion-free — pure state)
function useCountUp(target, duration = 1400) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!target || target < 0) { setN(0); return; }
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

function StatTile({ label, value, color, icon: Icon, suffix = "", testid }) {
  const n = useCountUp(value);
  return (
    <div className={`term-card ${color}`} data-testid={testid}>
      <div className="p-4 sm:p-5 relative">
        <div className="flex items-center justify-between mb-2">
          <div className="sc-label text-[9px]" style={{ color: `var(--sc-${color})` }}>{label}</div>
          <Icon className="w-3.5 h-3.5" style={{ color: `var(--sc-${color})` }} />
        </div>
        <div
          className={`sc-mono text-3xl sm:text-4xl font-bold counter-glow`}
          style={{ color: `var(--sc-${color})` }}
        >
          {n.toLocaleString()}<span className="text-lg opacity-70">{suffix}</span>
        </div>
      </div>
    </div>
  );
}

function EmptyStateCard({ title, cta, to, note, color = "volt", icon: Icon = Terminal }) {
  return (
    <Link
      to={to}
      className="term-card empty-pulse block p-8 text-center"
      data-testid={`empty-${title.toLowerCase().replace(/\s+/g,"-")}`}
    >
      <Icon className="w-10 h-10 mx-auto mb-4" style={{ color: `var(--sc-${color})` }} />
      <div className="sc-heading text-xl font-black mb-2">{title}</div>
      <div className="text-xs text-zinc-500 mb-6 max-w-xs mx-auto leading-relaxed">{note}</div>
      <span className="sc-btn-ghost inline-flex items-center gap-2 text-xs">
        {cta} <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}

// Reveal on scroll (IntersectionObserver)
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add("in"));
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export default function Landing() {
  const [pulse, setPulse] = useState(null);
  const [games, setGames] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/games/catalog").then(r => setGames(r.data)).catch(() => {});
    const load = () => api.get("/landing/pulse").then(r => setPulse(r.data)).catch(() => {});
    load();
    // Faster refresh when someone is live so the hero preview stays fresh
    // (streamer uploads a snapshot every ~5s while broadcasting).
    let iv = setInterval(load, 5000);
    const t = setInterval(() => setTick(x => x + 1), 3000);
    return () => { clearInterval(iv); clearInterval(t); };
  }, []);

  // Ambient marquee messages
  const tickerA = [
    "◢ FREE-TO-PLAY · NO WAGERS · NO GAMBLING · JUST GAMES ◤",
    "▲ SPARKS ARE FREE · EARN BY WATCHING · PLAYING · CHATTING",
    "◈ GOLD COINS → TIPS · 100% TO VERIFIED STREAMERS",
    "▓▓ STRIPE IDENTITY 18+ KYC · CREATOR SAFETY FIRST",
    "◤ WEBRTC · SUB-SECOND LATENCY · CAMERA + MIC + SCREEN",
    "▶ 11 GAMES ON DECK · MORE ADDED MONTHLY",
    "★ +100 BONUS SPARKS FOR EVERY $10 IN COIN PACKS",
    "◆ CUSTOM BLING · BORDERS · CHAT COLORS · EMOTE PACKS",
  ];
  const tickerB = (() => {
    if (!pulse) return ["INITIALIZING PULSE STREAM…"];
    const out = [];
    (pulse.recent_signups || []).forEach(s => out.push(`◉ ${s.username} joined the arcade`));
    (pulse.recent_tips || []).forEach(t => out.push(`♦ ${t.from_username} tipped ${t.to_username} ${t.amount}g`));
    (pulse.recent_lfg || []).forEach(l => out.push(`▲ ${l.author_username} needs a party for ${GAME_NAMES[l.game_slug] || l.game_slug}`));
    return out.length ? out : ["THE ARCADE JUST BOOTED · BE THE FIRST TO SPARK A SIGNAL"];
  })();

  const liveChannels = pulse?.live_channels || [];
  const recentLfg = pulse?.recent_lfg || [];
  const featured = pulse?.featured_streamers || [];
  const trending = pulse?.trending_games || [];

  const heroRef = useReveal();
  const statsRef = useReveal();
  const liveRef = useReveal();
  const lfgRef = useReveal();
  const gamesRef = useReveal();
  const econRef = useReveal();
  const proofRef = useReveal();
  const ctaRef = useReveal();
  const parallax = useMouseParallax();

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0A0C]" data-testid="landing-root">
      {/* ================================================================ */}
      {/* HERO: CRT grid + neon orbs + glitch title                         */}
      {/* ================================================================ */}
      <section className="relative overflow-hidden" style={{ minHeight: "92vh" }}>
        {/* Neon orbs with mouse-parallax drift */}
        <div className="neon-orb" style={{ background: "#FF1053", width: 340, height: 340, top: -80, left: -80, transform: `translate3d(${parallax.x * 30}px, ${parallax.y * 30}px, 0)`, transition: "transform 250ms ease-out" }} />
        <div className="neon-orb" style={{ background: "#CCFF00", width: 280, height: 280, top: 100, right: -60, animationDelay: "-3s", transform: `translate3d(${parallax.x * -40}px, ${parallax.y * -25}px, 0)`, transition: "transform 250ms ease-out" }} />
        <div className="neon-orb" style={{ background: "#00F0FF", width: 220, height: 220, bottom: 40, left: "40%", animationDelay: "-6s", transform: `translate3d(${parallax.x * 55}px, ${parallax.y * -35}px, 0)`, transition: "transform 250ms ease-out" }} />
        {/* Interactive particle mesh */}
        <div className="absolute inset-0 z-0"><HeroParticles /></div>
        {/* Grid floor */}
        <div className="cyber-grid" />
        <div className="crt-scanlines" />
        {/* Floating sparks */}
        <div className="hero-sparks" aria-hidden="true">
          {[...Array(24)].map((_, i) => (
            <span key={i} className="hero-spark" style={{ left: `${(i * 4.2) % 100}%`, animationDelay: `${(i * 0.7) % 8}s`, animationDuration: `${6 + (i % 5)}s` }} />
          ))}
        </div>

        <div ref={heroRef} className="reveal relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24">
          {/* Top bar — 6 meaningful platform chips */}
          <div className="flex items-center gap-2.5 mb-10 flex-wrap">
            <span className="chip-neon volt flicker-slow" data-testid="chip-live-now">
              <span className="rec-dot" style={{ background: "#CCFF00", boxShadow: "0 0 12px #CCFF00" }} /> Live Now
            </span>
            <span className="chip-neon orange" data-testid="chip-age-verified">
              <ShieldCheck className="w-3 h-3" /> Age Verified
            </span>
            <span className="chip-neon pink" data-testid="chip-streamer-first">
              <Radio className="w-3 h-3" /> Streamer First
            </span>
            <span className="chip-neon cyan" data-testid="chip-low-latency">
              <Zap className="w-3 h-3" /> Low Latency
            </span>
            <span className="chip-neon volt" data-testid="chip-party-rooms">
              <Users className="w-3 h-3" /> Party Rooms
            </span>
            <span className="chip-neon pink" data-testid="chip-2fa">
              <Cpu className="w-3 h-3" /> 2FA Protected
            </span>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="sc-label mb-4" style={{ color: "var(--sc-orange)" }}>◢ WHERE CREATORS BUILD COMMUNITIES</div>
              <h1 className="pixel-title text-[clamp(3rem,10vw,8rem)]" data-testid="hero-title">
                <span className="glitch">LOCK</span>{" "}<span className="glitch">IN.</span><br />
                <span style={{ color: "var(--sc-volt)" }} className="text-glow-volt">PLAY LOUD.</span>{" "}
                <span style={{ color: "var(--sc-pink)" }} className="text-glow-pink">RUN THE ROOM.</span>
              </h1>
              <div className="mt-6 sc-heading text-xl sm:text-2xl font-bold" data-testid="hero-rotator">
                <span className="text-zinc-400">You are the </span>
                <Typewriter
                  words={["host.", "gamer.", "hype squad.", "prize pool.", "vibe.", "reason people log in."]}
                  color="var(--sc-volt)"
                  className="text-glow-volt"
                />
              </div>
              <p className="text-zinc-300 text-lg max-w-2xl mt-8 leading-relaxed">
                MystiCast is the streaming arcade where <span className="text-white font-semibold">creators host the game</span>, <span className="text-white font-semibold">the viewers play too</span>, and every tip goes 100% to the streamer. Camera live. Cards dealt. Chat blazing. <span style={{ color: "var(--sc-volt)" }} className="font-bold">No gambling. All hype.</span>
              </p>
              <div className="flex flex-wrap gap-3 mt-10 items-center">
                <Link data-testid={TIDS.landingCtaSignup} to="/signup" className="sc-btn text-base flex items-center gap-2">
                  <Rocket className="w-4 h-4" /> Boot Your Channel
                </Link>
                <Link to="/games" className="sc-btn-volt sc-btn text-base flex items-center gap-2" data-testid="landing-cta-games">
                  <Gamepad2 className="w-4 h-4" /> Enter The Arcade
                </Link>
                <Link data-testid={TIDS.landingCtaBrowse} to="/browse" className="sc-btn-ghost text-base flex items-center gap-2">
                  <Radio className="w-4 h-4" /> See Who&apos;s Live
                </Link>
              </div>

              {/* Global search — friends, streamers, games */}
              <div className="mt-8 max-w-2xl">
                <div className="sc-label mb-2" style={{ color: "var(--sc-cyan)" }}>
                  ◈ QUICK SEARCH · FRIENDS · STREAMERS · GAMES
                </div>
                <GlobalSearch />
              </div>
            </div>

            {/* Right: floating live snippet + arcade panel */}
            <div className="lg:col-span-4 relative">
              <div className="term-card bracket-corners p-5 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="sc-label" style={{ color: "var(--sc-volt)" }}>▶ PULSE_STREAM</div>
                  <Wifi className="w-4 h-4 text-[color:var(--sc-volt)]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="sc-label text-[9px] mb-1">LIVE NOW</div>
                    <div className="sc-mono text-3xl font-bold counter-glow" style={{ color: "var(--sc-pink)" }}>
                      {pulse?.live_streams ?? "--"}
                    </div>
                  </div>
                  <div>
                    <div className="sc-label text-[9px] mb-1">VIEWERS</div>
                    <div className="sc-mono text-3xl font-bold counter-glow" style={{ color: "var(--sc-cyan)" }}>
                      {pulse?.viewers_now ?? "--"}
                    </div>
                  </div>
                  <div>
                    <div className="sc-label text-[9px] mb-1">CREATORS</div>
                    <div className="sc-mono text-3xl font-bold counter-glow" style={{ color: "var(--sc-volt)" }}>
                      {pulse?.verified_streamers ?? "--"}
                    </div>
                  </div>
                  <div>
                    <div className="sc-label text-[9px] mb-1">LFG POSTS</div>
                    <div className="sc-mono text-3xl font-bold counter-glow" style={{ color: "var(--sc-orange)" }}>
                      {pulse?.lfg_open ?? "--"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mini live channel preview — top streamer's actual video */}
              <div className="term-card pink p-0 overflow-hidden">
                <div className="flex items-center gap-2 px-4 pt-4 mb-2">
                  <span className="rec-dot" />
                  <div className="sc-label" style={{ color: "var(--sc-pink)" }}>ON_AIR</div>
                  {liveChannels[0] && (
                    <span className="sc-mono text-[9px] text-zinc-500 ml-auto">
                      {(liveChannels[0].viewer_count || 0).toLocaleString()} watching
                    </span>
                  )}
                </div>
                {liveChannels[0] ? (
                  <HeroLivePreview channel={liveChannels[0]} />
                ) : (
                  <div className="px-4 pb-4">
                    <div className="sc-heading text-base font-bold text-zinc-400">Stage is empty.</div>
                    <div className="text-xs text-zinc-500 mt-1">Your face here — first go-live gets the spotlight.</div>
                    <Link to="/signup" className="chip-neon pink mt-3 inline-flex hover:brightness-125">CLAIM IT →</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ticker A — value props */}
        <div className="relative border-t border-b border-white/10 bg-[#0A0A0C] py-3">
          <div className="marquee ticker-thin">
            {[0,1].map(i => (
              <div key={i} className="marquee__track">
                {tickerA.map((m, k) => (
                  <span key={k} className="sc-mono text-[11px] tracking-widest text-zinc-400 whitespace-nowrap flex items-center gap-4">
                    {m}<span className="text-[color:var(--sc-volt)]">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* PULSE STATS — animated counters                                   */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 relative">
        <div className="mb-6">
          <CommunityStatsRow pulse={pulse} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 mb-8">
          <TrendingCreators />
          <LiveActivityTicker />
        </div>
      </section>

      {/* ================================================================ */}
      {/* PULSE STATS — animated counters                                   */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 relative">
        <div ref={statsRef} className="reveal">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>◈ ECOSYSTEM PULSE · REAL-TIME</div>
              <h2 className="sc-heading text-4xl lg:text-5xl font-black mt-2 tracking-tight">The arcade never sleeps.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label="◉ NODES ONLINE" value={pulse?.total_users || 0} color="volt" icon={Users} testid="stat-users" />
            <StatTile label="✓ VERIFIED CREATORS" value={pulse?.verified_streamers || 0} color="pink" icon={ShieldCheck} testid="stat-creators" />
            <StatTile label="SPARKS MINTED" value={pulse?.sparks_awarded || 0} color="cyan" icon={Sparkles} testid="stat-sparks" />
            <StatTile label="♦ GOLD TIPPED" value={pulse?.gold_tipped || 0} color="orange" icon={Coins} suffix="g" testid="stat-tipped" />
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* LIVE STAGE — real channels                                        */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 relative">
        <div ref={liveRef} className="reveal">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="sc-label flex items-center gap-2" style={{ color: "var(--sc-pink)" }}>
                <span className="rec-dot" /> ON_AIR · LIVE FEEDS
              </div>
              <h2 className="sc-heading text-4xl lg:text-5xl font-black mt-2 tracking-tight">The room is <span style={{ color: "var(--sc-pink)" }} className="text-glow-pink">hot</span>.</h2>
            </div>
            <Link to="/browse" className="sc-btn-ghost text-xs">ALL LIVE →</Link>
          </div>

          {liveChannels.length === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <EmptyStateCard
                title="No stream on air. Yet."
                note="This grid lights up the moment a creator boots up. Be the one everyone remembers walking in on."
                cta="GO LIVE NOW"
                to="/signup"
                color="pink"
                icon={Radio}
              />
              <div className="term-card p-8 lg:col-span-2 flex items-center gap-6">
                <div className="hidden sm:block flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-2 border-[color:var(--sc-volt)] flex items-center justify-center anim-pulse-volt">
                    <Cpu className="w-7 h-7 text-[color:var(--sc-volt)]" />
                  </div>
                </div>
                <div>
                  <div className="sc-label" style={{ color: "var(--sc-volt)" }}>◤ WHAT YOU GET</div>
                  <div className="sc-heading text-2xl font-black mt-1">WebRTC · Sub-second · Guest-on-stage</div>
                  <div className="text-sm text-zinc-400 mt-2 max-w-lg leading-relaxed">
                    Camera + mic + screen share. Bring viewers up to your stage. Play the game with them. All in browser, zero installs.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveChannels.map((c) => (
                <Link
                  key={c.username}
                  to={`/c/${c.username}`}
                  data-testid={`live-card-${c.username}`}
                  className="term-card pink block overflow-hidden group"
                >
                  <div className="aspect-video bg-[#18181C] relative overflow-hidden">
                    <div className="absolute inset-0 cyber-grid opacity-30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-6xl font-black opacity-20" style={{ color: "var(--sc-pink)" }}>{c.username.slice(0,2).toUpperCase()}</div>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="chip-neon pink flicker-slow"><span className="rec-dot" style={{ width: 6, height: 6 }} /> LIVE</span>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <span className="chip-neon cyan"><Eye className="w-2.5 h-2.5" /> {c.viewer_count}</span>
                    </div>
                    {c.game_slug && (
                      <div className="absolute bottom-3 left-3">
                        <span className="chip-neon volt">{GAME_NAMES[c.game_slug] || c.game_slug}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="sc-heading text-base font-bold truncate group-hover:text-[color:var(--sc-volt)] transition">{c.title}</div>
                    <div className="sc-mono text-xs text-zinc-500 mt-1">@{c.username} · {c.category}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <hr className="neon-hr max-w-7xl mx-auto" />

      {/* ================================================================ */}
      {/* LFG BOARD                                                         */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div ref={lfgRef} className="reveal">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="sc-label" style={{ color: "var(--sc-orange)" }}>▲ LFG · LOOKING FOR GROUP</div>
              <h2 className="sc-heading text-4xl lg:text-5xl font-black mt-2 tracking-tight">Find your <span style={{ color: "var(--sc-orange)" }}>party</span>.</h2>
            </div>
            <Link to="/lfg" className="sc-btn-ghost text-xs">BROWSE ALL →</Link>
          </div>

          {recentLfg.length === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <EmptyStateCard
                title="No LFG posts. Pin yours first."
                note="Cast a signal — hosts and players will lock on. Watch parties, game runs, tournament crews."
                cta="POST A CALL"
                to="/lfg"
                color="orange"
                icon={MessageSquare}
              />
              <div className="term-card orange p-6 lg:col-span-2">
                <div className="sc-label" style={{ color: "var(--sc-orange)" }}>◈ HOW IT WORKS</div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {[
                    { n: "01", t: "Cast", d: "Pick a game, say what you need — teammates, viewers, hype squad." },
                    { n: "02", t: "Match", d: "Apply or invite. See applicant list. Approve who fits." },
                    { n: "03", t: "Play", d: "Everyone jumps into the channel. Stream + game + chat, one click." },
                  ].map((s) => (
                    <div key={s.n}>
                      <div className="sc-mono text-2xl font-black text-[color:var(--sc-orange)]">{s.n}</div>
                      <div className="sc-heading text-sm font-bold mt-2">{s.t}</div>
                      <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{s.d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentLfg.map((p) => (
                <Link
                  key={p.id}
                  to="/lfg"
                  data-testid={`lfg-card-${p.id}`}
                  className="term-card orange block p-5"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="chip-neon orange">{p.kind === "invite" ? "HOST" : "SEEK"}</span>
                    <span className="sc-mono text-[10px] text-zinc-500">/{p.game_slug}</span>
                  </div>
                  <div className="sc-heading text-base font-bold leading-tight line-clamp-2">{p.title}</div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                    <div className="sc-mono text-xs text-zinc-500">@{p.author_username}</div>
                    <div className="chip-neon volt"><Users className="w-2.5 h-2.5" />{p.slots || "?"}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <hr className="neon-hr max-w-7xl mx-auto" />

      {/* ================================================================ */}
      {/* GAME DECK                                                          */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div ref={gamesRef} className="reveal">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="sc-label" style={{ color: "var(--sc-volt)" }}>▶ GAME DECK · 11 PLAYABLE</div>
              <h2 className="sc-heading text-4xl lg:text-5xl font-black mt-2 tracking-tight">Games <span style={{ color: "var(--sc-volt)" }}>built into every channel</span>.</h2>
              <p className="text-sm text-zinc-400 mt-3 max-w-xl">Chess. Hold&apos;em. Slots. Checkers. Fire them up mid-stream and drop viewers straight into the seat next to you.</p>
            </div>
            <Link to="/games" className="sc-btn-ghost text-xs">SEE ALL →</Link>
          </div>

          {/* Trending strip (data-driven) */}
          {trending.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <span className="sc-label" style={{ color: "var(--sc-pink)" }}>🔥 TRENDING:</span>
              {trending.map((t) => (
                <span key={t.game_slug} className="chip-neon pink flicker-slow">
                  {GAME_NAMES[t.game_slug] || t.game_slug} · {t.count} live
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 stagger">
            {games.map((g, i) => (
              <Link
                key={g.slug}
                to="/games"
                data-testid={`landing-game-${g.slug}`}
                className={`term-card block p-4 relative ${!g.available ? "opacity-40" : ""}`}
              >
                <div className="sc-mono text-[9px] text-zinc-600">#{String(i+1).padStart(2,"0")}</div>
                <div className="sc-heading text-sm font-bold mt-2 leading-tight">{g.name}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{g.players} · {g.category}</div>
                {g.available ? (
                  <div className="text-[9px] tracking-[0.2em] uppercase font-bold mt-3" style={{ color: "var(--sc-volt)" }}>▶ PLAY</div>
                ) : (
                  <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 mt-3">▓ QUEUED</div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <hr className="neon-hr max-w-7xl mx-auto" />

      {/* ================================================================ */}
      {/* DUAL ECONOMY                                                       */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div ref={econRef} className="reveal">
          <div className="mb-8">
            <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>◆ THE DUAL ECONOMY</div>
            <h2 className="sc-heading text-4xl lg:text-5xl font-black mt-2 tracking-tight">Two currencies. <span style={{ color: "var(--sc-cyan)" }}>Zero gambling.</span></h2>
            <p className="text-sm text-zinc-400 mt-3 max-w-2xl">Sparks are free — earned by playing, watching, showing up. Gold Coins are purchased and tipped 100% to verified streamers. Never the twain shall meet.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="term-card cyan p-6 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl" style={{ background: "#00F0FF", opacity: 0.15 }} />
              <div className="flex items-center gap-3 mb-4 relative z-10">
                <Sparkles className="w-6 h-6 text-[color:var(--sc-cyan)]" />
                <div className="sc-heading text-2xl font-black">SPARKS</div>
                <span className="chip-neon cyan ml-auto">FREE-PLAY</span>
              </div>
              <p className="text-sm text-zinc-300 relative z-10">Free engagement currency. Watch → mint. Play → mint. Chat → mint. Spend them in the Bling Shop on borders, chat colors, animated profile fx.</p>
              <div className="mt-6 grid grid-cols-3 gap-3 relative z-10">
                <div><div className="sc-mono text-2xl font-bold text-[color:var(--sc-cyan)]">+1</div><div className="text-[10px] text-zinc-500 mt-1">/ min watched</div></div>
                <div><div className="sc-mono text-2xl font-bold text-[color:var(--sc-cyan)]">+50</div><div className="text-[10px] text-zinc-500 mt-1">/ level-up bonus</div></div>
                <div><div className="sc-mono text-2xl font-bold text-[color:var(--sc-cyan)]">500</div><div className="text-[10px] text-zinc-500 mt-1">signup welcome</div></div>
              </div>
            </div>
            <div className="term-card orange p-6 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl" style={{ background: "#FF5900", opacity: 0.15 }} />
              <div className="flex items-center gap-3 mb-4 relative z-10">
                <Coins className="w-6 h-6 text-[color:var(--sc-orange)]" />
                <div className="sc-heading text-2xl font-black">GOLD COINS ♦</div>
                <span className="chip-neon orange ml-auto">100% → CREATOR</span>
              </div>
              <p className="text-sm text-zinc-300 relative z-10">Purchased currency. Tip your favorites. Every gold coin sent = 100% to the streamer, cash-out on real Stripe payouts. Verification required both sides.</p>
              <div className="mt-6 grid grid-cols-3 gap-3 relative z-10">
                <div><div className="sc-mono text-2xl font-bold text-[color:var(--sc-orange)]">100%</div><div className="text-[10px] text-zinc-500 mt-1">to streamer</div></div>
                <div><div className="sc-mono text-2xl font-bold text-[color:var(--sc-orange)] inline-flex items-center gap-1">+100<Sparkles className="w-4 h-4" /></div><div className="text-[10px] text-zinc-500 mt-1">bonus / $10</div></div>
                <div><div className="sc-mono text-2xl font-bold text-[color:var(--sc-orange)]">KYC</div><div className="text-[10px] text-zinc-500 mt-1">Stripe Identity</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* FEATURED CREATORS / SOCIAL PROOF                                   */}
      {/* ================================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div ref={proofRef} className="reveal">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="sc-label" style={{ color: "var(--sc-volt)" }}>★ VERIFIED CREATORS</div>
              <h2 className="sc-heading text-4xl lg:text-5xl font-black mt-2 tracking-tight">The founding class.</h2>
            </div>
          </div>
          {featured.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <EmptyStateCard
                title="Founder slots open."
                note="First verified creators lock in as Founders. Permanent badge. Featured across the platform."
                cta="VERIFY & GO LIVE"
                to="/signup"
                color="volt"
                icon={Trophy}
              />
              <div className="term-card p-6 md:col-span-2">
                <div className="sc-label" style={{ color: "var(--sc-volt)" }}>WHAT VERIFICATION UNLOCKS</div>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  {[
                    ["Go Live", "WebRTC broadcast with unlimited viewers"],
                    ["Accept Tips", "Real Stripe payouts, 100% split"],
                    ["Founder Badge", "Permanent status for early creators"],
                    ["No Coin Cap", "Unverified users capped at 25k gold"],
                  ].map(([t,d]) => (
                    <div key={t} className="flex gap-3">
                      <div className="w-8 h-8 rounded border border-[color:var(--sc-volt)] flex items-center justify-center flex-shrink-0">
                        <Flame className="w-4 h-4 text-[color:var(--sc-volt)]" />
                      </div>
                      <div>
                        <div className="sc-heading font-bold text-sm">{t}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {featured.map((f) => (
                <Link
                  key={f.username}
                  to={`/c/${f.username}`}
                  className="term-card block p-5 text-center"
                  data-testid={`featured-${f.username}`}
                >
                  <div className="w-16 h-16 mx-auto rounded-full border-2 border-[color:var(--sc-volt)] flex items-center justify-center bg-[#0A0A0C] anim-pulse-volt">
                    {f.avatar_url ? (
                      <img src={f.avatar_url} alt="" className="w-full h-full rounded-full object-cover"  loading="lazy" decoding="async" />
                    ) : (
                      <span className="sc-heading text-2xl font-black text-[color:var(--sc-volt)]">
                        {f.username.slice(0,2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="sc-heading text-sm font-bold mt-3 truncate">@{f.username}</div>
                  <div className="sc-mono text-[10px] text-zinc-500 mt-1">{f.wins || 0} wins</div>
                  <div className="chip-neon volt mt-2 inline-flex"><ShieldCheck className="w-2.5 h-2.5" /> VERIFIED</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Live signal ticker (recent tips + signups) */}
      <div className="relative border-t border-b border-white/10 bg-[#0A0A0C] py-3">
        <div className="marquee marquee-reverse ticker-thin">
          {[0,1].map(i => (
            <div key={i} className="marquee__track">
              {tickerB.map((m, k) => (
                <span key={k} className="sc-mono text-[11px] tracking-widest text-zinc-400 whitespace-nowrap flex items-center gap-4">
                  {m}<span className="text-[color:var(--sc-pink)]">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* FINAL CTA                                                          */}
      {/* ================================================================ */}
      <section className="relative overflow-hidden">
        <div className="cyber-grid" />
        <div className="crt-scanlines" />
        <div className="neon-orb" style={{ background: "#FF1053", width: 400, height: 400, top: -100, left: "20%" }} />
        <div className="neon-orb" style={{ background: "#CCFF00", width: 340, height: 340, bottom: -120, right: "15%", animationDelay: "-4s" }} />
        <div ref={ctaRef} className="reveal relative z-10 max-w-4xl mx-auto px-6 py-28 text-center">
          <div className="sc-label mb-4" style={{ color: "var(--sc-pink)" }}>◢ INSERT COIN TO CONTINUE</div>
          <h2 className="pixel-title text-[clamp(3rem,8vw,6.5rem)]">
            <span className="glitch">STOP</span>{" "}
            <span className="glitch">WATCHING.</span><br />
            <span style={{ color: "var(--sc-volt)" }} className="text-glow-volt">START RUNNING</span><br />
            <span style={{ color: "var(--sc-pink)" }} className="text-glow-pink">THE ROOM.</span>
          </h2>
          <p className="text-zinc-400 mt-6 max-w-xl mx-auto">
            Your channel. Your rules. Your games. Your tips. Five minutes to set up, forever to build. Bring your Steam · Twitch · Kick · TikTok — plant your flag here.
          </p>
          <div className="flex justify-center gap-3 mt-10 flex-wrap">
            <Link to="/signup" className="sc-btn text-base flex items-center gap-2" data-testid="landing-cta-final-signup">
              <Zap className="w-4 h-4" /> Claim Your Channel
            </Link>
            <Link to="/browse" className="sc-btn-volt sc-btn text-base flex items-center gap-2">
              <Radio className="w-4 h-4" /> See Who&apos;s On
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * HeroLivePreview — the small hero-column live tile.
 * - If the top streamer is on OBS/HLS, embeds an autoplay-muted HLS player.
 * - Otherwise shows their thumbnail (or a gradient fallback) with a LIVE chip.
 * - Whole tile links to the full watch page.
 */
function HeroLivePreview({ channel }) {
  const isObs = channel?.live_source === "obs";
  const initial = (channel.username || "?")[0].toUpperCase();
  // The snapshot updates every ~5s server-side. Force re-decode by mutating
  // the src (cache-buster) whenever the snapshot timestamp changes.
  const snapSrc = channel.live_snapshot_url
    ? `${channel.live_snapshot_url}#${channel.live_snapshot_at || ""}`
    : (channel.thumbnail_url || null);
  return (
    <Link
      to={`/c/${channel.username}/live`}
      className="block group"
      data-testid="hero-live-preview"
    >
      <div className="relative aspect-video bg-black overflow-hidden">
        {isObs ? (
          <ObsHlsPlayer username={channel.username} className="absolute inset-0 w-full h-full" />
        ) : snapSrc ? (
          <img src={snapSrc} alt={channel.username} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, hsl(${((channel.username || "").length * 47) % 360},70%,25%), hsl(${((channel.username || "").length * 47 + 60) % 360},70%,15%))`,
            }}
          />
        )}
        {/* Overlay: LIVE chip */}
        <span className="absolute top-2 left-2 chip-neon pink flex items-center gap-1.5 text-[10px] z-10">
          <span className="live-dot" /> LIVE
        </span>
      </div>
      {/* Meta strip */}
      <div className="px-4 py-3 border-t border-white/10">
        <div className="sc-heading text-sm font-bold truncate">{channel.title || "Live on MystiCast"}</div>
        <div className="sc-mono text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1.5 truncate">
          <span className="w-4 h-4 rounded-full inline-flex items-center justify-center text-[8px] font-bold overflow-hidden" style={{ background: "linear-gradient(135deg,#FF1053,#FF5900)" }}>
            {channel.avatar_url ? <img src={channel.avatar_url} alt="" className="w-full h-full object-cover" /> : initial}
          </span>
          @{channel.username} · {channel.category || "Just Chatting"}
        </div>
      </div>
    </Link>
  );
}

